import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useHistory, useLocation, Link } from 'react-router-dom';

import { ReactComponent as MabelLogo } from '../images/Mabel_Logo_White_RGB.svg';
import { mabel_dark_blue } from '../colors';

import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  ModalProps,
  notification,
  Spin,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  LockOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { Auth } from 'aws-amplify';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { CognitoUser, ChallengeName } from 'amazon-cognito-identity-js';
import { useRequest } from 'ahooks';
import useUrlState from '../hooks/useUrlState';
import { useForm } from 'antd/lib/form/Form';
import axios from 'axios';

type LoginPageState = {
  method?: 'password' | 'forgot';
  username?: string;
  code?: string;
  error?: string;
  error_description?: string;
};

const LoginPage: React.FC = () => {
  const [urlState, setUrlState] = useUrlState<LoginPageState>(
    {
      method: 'password',
      username: undefined,
      code: undefined,
      error: undefined,
      error_description: undefined,
    },
    { navigateMode: 'replace' }
  );
  const history = useHistory();
  const location = useLocation<{ from: Location }>();
  const { from } = location.state || { from: { pathname: '/' } };

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for error in URL params manually to catch it before Amplify/refresh clears it
    const params = new URLSearchParams(window.location.search);
    const urlError = params.get('error');
    const urlErrorDesc = params.get('error_description');

    if (urlError || urlErrorDesc) {
      sessionStorage.setItem(
        'auth_error',
        JSON.stringify({ error: urlError, error_description: urlErrorDesc })
      );
    }

    const storedErrorStr = sessionStorage.getItem('auth_error');
    if (storedErrorStr) {
      const storedError = JSON.parse(storedErrorStr);
      // Optional: strip the noisy Cognito prefix "PreSignUp failed with error "
      let raw = storedError.error_description || storedError.error || '';
      const prefix = 'PreSignUp failed with error ';
      if (raw.startsWith(prefix)) {
        raw = raw.slice(prefix.length).trim();
      }

      notification.error({
        message: 'Login error',
        description: raw,
        duration: 0,
      });
      sessionStorage.removeItem('auth_error');
    }

    if (
      !urlState.error &&
      !urlState.error_description &&
      !storedErrorStr &&
      !urlError
    ) {
      // On login page load, check if user is already logged in, and if so redirect
      Auth.currentSession()
        .then((user) => history.replace(from))
        .catch((err) => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [history, from, setUrlState, urlState.error, urlState.error_description]);

  const [isMFAModalVisible, setIsMFAModalVisible] = useState(false);

  const [emailStep, setEmailStep] = useState<'email' | 'password'>('email');
  const [email, setEmail] = useState('');

  const federatedSigninRequest = useRequest(
    async (provider: string) => {
      if (provider === 'Google') {
        await Auth.federatedSignIn({
          provider: CognitoHostedUIIdentityProvider.Google,
        });
      } else {
        await Auth.federatedSignIn({
          customProvider: provider,
        });
      }
    },
    {
      manual: true,
      onError: (err) => {
        const message =
          err instanceof Error
            ? err.message
            : 'We were unable to start sign-in. Please try again.';
        notification.error({
          message: 'Sign-In Error',
          description: message,
          duration: 0,
        });
      },
    }
  );

  const checkEmailRequest = useRequest(
    async (email: string) => {
      const response = await axios.post(
        `${process.env.REACT_APP_CHALICE_URL}/idp_redirect`,
        { input: { email } }
      );
      return response.data;
    },
    {
      manual: true,
      onSuccess: (data) => {
        if (data.redirect === 'Email/Password') {
          setEmailStep('password');
        } else {
          federatedSigninRequest.run(data.redirect);
        }
      },
      onError: () => {
        setEmailStep('password');
      },
    }
  );

  const signInUser = (email: string, pass: string) => {
    return new Promise<CognitoUser>(async (resolve, reject) => {
      try {
        const user = await Auth.signIn(email, pass);
        if (user.challengeName === 'SOFTWARE_TOKEN_MFA') {
          setIsMFAModalVisible(true);
        } else if (!user.challengeName) {
          await Auth.currentSession();
          history.replace(from);
        }
        resolve(user as CognitoUser);
      } catch (err) {
        reject(err);
      }
    });
  };

  const signInWithPassword = useRequest(signInUser, {
    manual: true,
    // onSuccess: (data, params) => history.replace(from),
  });
  const initForgotPassword = useRequest((email) => Auth.forgotPassword(email), {
    manual: true,
  });
  const completeForgotPassword = useRequest(
    (username, code, pass) => Auth.forgotPasswordSubmit(username, code, pass),
    {
      manual: true,
      onSuccess: (data, [username, code, pass]) =>
        signInWithPassword.run(username, pass),
    }
  );

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: mabel_dark_blue,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: mabel_dark_blue,
      }}
    >
      <Helmet>
        <title>Login | Mabel</title>
      </Helmet>
      <div style={{ width: '60%', maxWidth: 400, margin: 36 }}>
        <MabelLogo />
      </div>
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          padding: 32,
          backgroundColor: 'white',
          borderRadius: 8,
        }}
      >
        {urlState.method === 'forgot' && !urlState.code ? (
          <Form
            name="password_reset"
            layout="vertical"
            requiredMark={false}
            style={{ padding: 16 }}
            onFinish={(values) => initForgotPassword.run(values.email)}
          >
            <Form.Item>
              <Typography.Title level={4}>Reset your password</Typography.Title>
            </Form.Item>
            <Form.Item
              name="email"
              label="Email Address"
              validateTrigger="onBlur"
              rules={[
                { required: true, message: 'Email required' },
                {
                  type: 'email',
                  message: 'Please input a valid email address',
                },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Email"
                size="large"
              />
            </Form.Item>
            <Form.Item>
              <Link to="/login">
                <Button type="link" icon={<ArrowLeftOutlined />}>
                  Back to login
                </Button>
              </Link>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={initForgotPassword.loading}
              >
                Send reset link
              </Button>
            </Form.Item>
            {initForgotPassword.error && (
              <Alert
                type="error"
                message={initForgotPassword.error.message}
                showIcon
              />
            )}
            {initForgotPassword.data && (
              <Alert
                type="success"
                message="Please click the link in your email"
                showIcon
              />
            )}
          </Form>
        ) : urlState.method === 'forgot' && urlState.code ? (
          <Form
            name="password_reset_complete"
            layout="vertical"
            requiredMark={false}
            style={{ padding: 16 }}
            validateTrigger="onBlur"
            onFinish={(values) =>
              completeForgotPassword.run(
                urlState.username,
                urlState.code,
                values.password
              )
            }
          >
            <Form.Item>
              <Typography.Title level={4}>
                Finish changing your password
              </Typography.Title>
            </Form.Item>
            <Form.Item
              name="password"
              label="New Password"
              rules={[
                {
                  required: true,
                  message: 'Please input a new password',
                },
                {
                  min: 10,
                  message: 'Password must be at least 10 characters',
                },
                {
                  pattern: /(?=.*[0-9])/,
                  message: 'Password must contain at least one digit 0-9',
                },
                {
                  //eslint-disable-next-line
                  pattern: /(?=.*[\^$*.\[\]{}\(\)?\-“!@#%&\/,><\’:;|_~`])/,
                  message:
                    'Password must contain at least one special character',
                },
              ]}
            >
              <Input.Password size="large" />
            </Form.Item>
            <Form.Item
              name="confirm"
              label="Confirm Password"
              dependencies={['password']}
              validateTrigger="onBlur"
              rules={[
                {
                  required: true,
                  message: 'Please re-enter the new password',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('The passwords do not match')
                    );
                  },
                }),
              ]}
            >
              <Input.Password size="large" />
            </Form.Item>
            <Form.Item>
              <Link to="/login">
                <Button type="link" icon={<ArrowLeftOutlined />}>
                  Back to login
                </Button>
              </Link>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={
                  completeForgotPassword.loading || signInWithPassword.loading
                }
              >
                Change password
              </Button>
            </Form.Item>
            {completeForgotPassword.error && (
              <Alert
                type="error"
                message={completeForgotPassword.error.message}
                showIcon
              />
            )}
          </Form>
        ) : emailStep === 'email' ? (
          <Form
            name="email_login"
            layout="vertical"
            requiredMark={false}
            style={{ padding: 16 }}
            onFinish={(values) => {
              setEmail(values.email);
              checkEmailRequest.run(values.email);
            }}
            initialValues={{ email }}
          >
            <Form.Item
              name="email"
              label="Email Address"
              validateTrigger="onBlur"
              rules={[
                { required: true, message: 'Email required' },
                {
                  type: 'email',
                  message: 'Please input a valid email address',
                },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="Email"
                size="large"
                autoFocus
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={
                  checkEmailRequest.loading || federatedSigninRequest.loading
                }
                block
              >
                Next
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Form
            name="password_login"
            layout="vertical"
            requiredMark={false}
            style={{ padding: 16 }}
            onFinish={(values) =>
              signInWithPassword.run(email, values.password)
            }
            initialValues={{ email }}
          >
            <Form.Item name="email" label="Email Address">
              <Input
                prefix={<MailOutlined />}
                placeholder="Email"
                size="large"
                disabled
              />
            </Form.Item>
            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: 'Password required' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                type="password"
                placeholder="Password"
                size="large"
                autoFocus
              />
            </Form.Item>
            <Form.Item>
              <Typography.Text type="secondary">
                Forgot your password?{' '}
              </Typography.Text>
              <Button
                type="link"
                onClick={() => setUrlState({ method: 'forgot' })}
              >
                Reset password
              </Button>
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={signInWithPassword.loading}
                block
              >
                Log in
              </Button>
            </Form.Item>
            <Form.Item>
              <Button
                type="link"
                onClick={() => setEmailStep('email')}
                style={{ padding: 0 }}
              >
                Sign in with a different email
              </Button>
            </Form.Item>
            {signInWithPassword.error && (
              <Alert
                type="error"
                message={signInWithPassword.error.message}
                showIcon
              />
            )}
          </Form>
        )}
      </div>
      {signInWithPassword.data && (
        <MFAConfirmModal
          user={signInWithPassword.data}
          mfaType={signInWithPassword.data.challengeName}
          from={from}
          modalProps={{
            visible: isMFAModalVisible,
            onCancel: () => setIsMFAModalVisible(false),
          }}
        />
      )}
      <div style={{ color: 'white', paddingTop: 8 }}>
        By continuing, you are indicating that you accept our{' '}
        <a href="https://getmabel.com/legal/terms">terms of service</a>.
      </div>
    </div>
  );
};

const MFAConfirmModal: React.FC<{
  user: any;
  mfaType?: ChallengeName;
  from: Location;
  modalProps: ModalProps;
}> = ({ user, mfaType, from, modalProps }) => {
  const [form] = useForm();
  const history = useHistory();

  const confirmMFACode = async (
    user: any,
    code: string,
    mfaType?: ChallengeName
  ) => {
    try {
      if (mfaType !== 'SOFTWARE_TOKEN_MFA') {
        throw new Error(`${mfaType} not supported`);
      }
      await Auth.confirmSignIn(user, code, mfaType);
    } catch (err) {
      throw err;
    }
  };

  const [error, setError] = useState<Error>();
  const { run, cancel, loading } = useRequest(confirmMFACode, {
    manual: true,
    onBefore: () => setError(undefined),
    onError: setError,
    onSuccess: (data, params) => history.replace(from),
  });

  return (
    <Modal
      {...modalProps}
      title="Multi-Factor Authentication"
      okText="Submit"
      centered
      maskClosable={false}
      confirmLoading={loading}
      onOk={() => form.submit()}
      onCancel={(e) => {
        cancel();
        setError(undefined);
        form.resetFields();
        modalProps.onCancel?.(e);
      }}
    >
      <p>Enter an MFA code to complete sign-in:</p>
      <Form
        form={form}
        validateTrigger="onBlur"
        onFinish={({ code }) => run(user, code, mfaType)}
      >
        <Form.Item
          name="code"
          rules={[
            { required: true, message: 'Please input the code' },
            { pattern: /^\d+$/, message: 'Invalid code format' },
            { min: 6, message: 'Code must be at least 6 digits' },
          ]}
        >
          <Input placeholder="123456" />
        </Form.Item>
      </Form>
      {error && (
        <Typography.Text type="danger">Error: {error.message}</Typography.Text>
      )}
    </Modal>
  );
};

export default LoginPage;
